/** /auth/oauth-handoff?req=… — the console's half of the MCP sign-in (backend metaplan A7c,
 *  AIE plan §6 "Login handoff").
 *
 *  An MCP client (Claude, Cursor, …) starts an OAuth authorization at the authorization server;
 *  for an ops account the server sends the browser here with `req`, a signed, short-lived,
 *  single-use request. `RequireAuth` has already made sure there is a console session: a sign-in
 *  started here comes back here, through a forced MFA enrolment too (`returnTo`). The page posts
 *  `req` to `POST /v2/console/oauth/handoff/` and, with the one-time code it gets back, replaces
 *  itself with the authorization server's callback, where the user sees the consent screen.
 *
 *  It starts by itself — the consent screen is the confirmation, and the authorization server
 *  refuses a callback its own browser cookie does not match (login-CSRF). `req` and the code are
 *  credentials: the call goes straight to the API (never React Query's caches) and the page
 *  leaves history behind it, so Back cannot replay a used link. A refusal does not use `req` up,
 *  so after "Set up two-factor authentication" the same link works until it expires. */
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as oauthApi from '@/api/oauth';
import { config } from '@/config';
import { BrandLogo } from '@/layout/BrandLogo';
import { leaveAppFor } from '@/utils/leaveApp';

import { useAuth } from './AuthProvider';
import { locationReturnTo, withReturnTo } from './returnTo';

/** What the user can do about a failure. `restart`: only the MCP client can start a new sign-in. */
type Next = 'restart' | 'enroll' | 'retry' | 'none';

interface Failure {
  message: string;
  next: Next;
  /** Link to `/mcp-connections`, where the user's own MCP addresses are. */
  mcpConnections?: boolean;
}

const UNREACHABLE: Failure = {
  message: 'The console could not complete the sign-in. Try again.',
  next: 'retry',
};

/** Copy per failure code: the backend's envelope codes (authserver.openapi.yaml, console handoff)
 *  plus three of the page's own (`CLIENT_FAILURES`). */
export const HANDOFF_FAILURES: Record<string, Failure> = {
  invalid_request: { message: 'This sign-in link is invalid or has expired.', next: 'restart' },
  invalid_handoff_request: {
    message: 'This sign-in link is invalid or has expired.',
    next: 'restart',
  },
  handoff_request_used: { message: 'This sign-in link was already used.', next: 'restart' },
  mfa_required: {
    message: 'Set up two-factor authentication before you connect an MCP client to your account.',
    next: 'enroll',
  },
  // Tenant-bound MCP URLs: also without `console_mcp_ops_connect`.
  not_eligible: {
    message:
      'Your account may not connect MCP clients, or has no permission that MCP tools need. Ask an Ops Admin for access, then connect again from your MCP client.',
    next: 'none',
  },
  // A per-tenant ops address (`…/v1/<code>`) whose code names no tenant.
  tenant_mismatch: {
    message: 'The MCP address in your client names no tenant. Check the tenant code at its end.',
    next: 'restart',
    mcpConnections: true,
  },
  authserver_disabled: { message: 'MCP sign-in is not set up on this server.', next: 'none' },
  // The page's own:
  missing_request: { message: 'This sign-in link is incomplete.', next: 'restart' },
  unexpected_redirect: {
    message:
      'The server answered with an unexpected sign-in address, so the console did not follow it.',
    next: 'restart',
  },
  unreachable: UNREACHABLE,
};

export const CLIENT_FAILURES = ['missing_request', 'unexpected_redirect', 'unreachable'];

/** Any other 403 — a subject-status reason such as `not_staff`, for a session that outlived the
 *  account's access. */
const NOT_ALLOWED: Failure = { message: 'This account cannot connect MCP clients.', next: 'none' };

export function describeHandoffFailure(code: string, status: number): Failure {
  return HANDOFF_FAILURES[code] ?? (status === 403 ? NOT_ALLOWED : UNREACHABLE);
}

type Phase =
  | { kind: 'working' }
  | { kind: 'leaving' }
  | { kind: 'failed'; code: string; status: number };

/** Local development may run the authorization server on plain http. */
const ALLOW_HTTP = config.sentryEnv === 'development';

export function OAuthHandoffPage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const req = searchParams.get('req') ?? '';

  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<Phase>(
    req ? { kind: 'working' } : { kind: 'failed', code: 'missing_request', status: 0 },
  );
  // One POST per attempt, also under StrictMode's double-run of effects (a second POST would
  // answer 409 handoff_request_used once the first minted a code).
  const sentFor = useRef(-1);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!req || sentFor.current === attempt) return;
    sentFor.current = attempt;
    setPhase({ kind: 'working' });
    oauthApi.postHandoff(req).then(
      (r) => {
        if (!mounted.current) return;
        const target = oauthApi.handoffRedirectTarget(r.redirect_url, { allowHttp: ALLOW_HTTP });
        if (!target) {
          setPhase({ kind: 'failed', code: 'unexpected_redirect', status: 200 });
          return;
        }
        setPhase({ kind: 'leaving' });
        leaveAppFor(target);
      },
      (e: unknown) => {
        // A 401 has already sent the user to sign in (and back here afterwards).
        if (!mounted.current) return;
        const status = e instanceof ConsoleApiError ? e.status : 0;
        setPhase({ kind: 'failed', code: oauthApi.oauthErrorCode(e) ?? 'unreachable', status });
      },
    );
  }, [req, attempt]);

  const failure = phase.kind === 'failed' ? describeHandoffFailure(phase.code, phase.status) : null;

  return (
    <Container maxWidth="xs" sx={{ pt: 8 }}>
      <Stack alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <BrandLogo size={48} />
        <Typography variant="h5" component="h1">
          Connect an MCP client
        </Typography>
        {auth.user && (
          <Typography variant="body2" color="text.secondary" align="center">
            Signed in as {auth.user.email}
          </Typography>
        )}
      </Stack>

      {!failure && (
        <Stack alignItems="center" spacing={2} role="status" aria-live="polite">
          <CircularProgress size={28} />
          <Typography variant="body2">
            {phase.kind === 'leaving'
              ? 'Continuing to the sign-in server…'
              : 'Checking your access…'}
          </Typography>
        </Stack>
      )}

      {failure && (
        <Stack spacing={2}>
          <Alert severity={failure.next === 'enroll' ? 'warning' : 'error'}>
            {failure.message}
            {failure.next === 'restart' && ' Go back to your MCP client and connect again.'}
          </Alert>
          {failure.next === 'enroll' && (
            <Button
              variant="contained"
              onClick={() => navigate(withReturnTo('/auth/mfa/enroll', locationReturnTo(location)))}
            >
              Set up two-factor authentication
            </Button>
          )}
          {failure.next === 'retry' && (
            <Button variant="contained" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
          )}
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {failure.mcpConnections && (
              <MuiLink component={RouterLink} to="/mcp-connections" underline="hover">
                Open MCP connections
              </MuiLink>
            )}
            <MuiLink component={RouterLink} to="/" underline="hover">
              Back to the console
            </MuiLink>
          </Stack>
        </Stack>
      )}
    </Container>
  );
}
